import React from 'react';

/**
 * What the active /dashboard tab is for, said as the page header's subtitle.
 *
 * There used to be two hero cards: Dashboard drew its own for Overview, and
 * Analytics drew a second, static one for the other seven tabs, so six tabs
 * shared a heading that described none of them. Then it was one hero card per
 * tab under a tab bar with no page title at all. Now the page has a real H1
 * ("Dashboard") and this copy is the line under it: the sentence first, the
 * scope after it on a desktop.
 */

export interface HeroCopy {
  kicker: string;
  /** A string, or per role where the heading itself differs (Revenue: the business vs you). */
  title: string | ((superAdmin: boolean) => string);
  /** Text differs by role wherever the scope of the data does. */
  text: (superAdmin: boolean) => string;
}

const SCOPE = (superAdmin: boolean) =>
  superAdmin ? 'Platform-wide across every employer and recruiter.' : 'Scoped to the clients assigned to you.';

/** Keyed by TabKey. A missing entry falls back to the analytics default. */
export const TAB_HERO: Record<string, HeroCopy> = {
  overview: {
    kicker: 'Platform Overview',
    title: "Real-time snapshot of your platform's health.",
    text: (sa) =>
      sa
        ? 'Platform-wide health across employers, jobs, and hiring.'
        : 'Submissions, hires, and pipeline across Premium Hiring.',
  },
  funnel: {
    kicker: 'Hiring funnel',
    title: 'Where candidates convert, and where the pipeline leaks.',
    text: SCOPE,
  },
  pipeline: {
    kicker: 'Pipeline health',
    title: 'What is moving, what is stuck, and for how long.',
    text: SCOPE,
  },
  recruiter: {
    kicker: 'Recruiter performance',
    title: 'Who is carrying the book, and how it is converting.',
    text: (sa) =>
      sa ? 'Every recruiter, ranked by outcome rather than activity.' : 'Your submissions, interviews and hires.',
  },
  jobs: {
    kicker: 'Job performance',
    title: 'Which roles attract applicants, and which go quiet.',
    text: SCOPE,
  },
  quality: {
    kicker: 'Candidate quality',
    title: 'Whether the candidates reaching interview are the right ones.',
    text: SCOPE,
  },
  revenue: {
    kicker: 'Revenue / ROI',
    title: (sa) => (sa ? 'What the business earns, and what each HR brings in.' : 'What you bring in.'),
    text: (sa) =>
      sa
        ? 'Plan purchases and placement fees. A placement fee is one month or half a month of a placed candidate\u2019s salary, credited to their HR.'
        : 'The placement fees you have brought in: one month or half a month of salary for each candidate you placed who joined.',
  },
  cities: {
    kicker: 'Candidates By City',
    title: 'How many candidates each city has, and who they are.',
    text: () => 'Counted as lives in the city or one of its areas, or prefers it: the same rule as the Job Seekers City filter.',
  },
  activity: {
    kicker: 'Activity',
    title: 'Who did what, and how many people were active each day.',
    text: () => 'Super Admin only. Job seekers, employers and staff, kept for a year.',
  },
  alerts: {
    kicker: 'Alerts & risks',
    title: 'The things that need attention before they become problems.',
    text: SCOPE,
  },
  // Without this the Email tab fell through to FALLBACK and announced itself as "Hiring
  // analytics" — a heading describing a different dashboard, over email numbers.
  email: {
    kicker: 'Email',
    title: 'What the platform sent, and whether anyone opened it.',
    text: () => 'Super Admin only. Sends, opens and clicks across every template, last 30 days.',
  },
};

const FALLBACK: HeroCopy = {
  kicker: 'Hiring analytics',
  title: 'Where candidates convert, and where the pipeline leaks.',
  text: SCOPE,
};

export const heroCopyFor = (tab: string): HeroCopy => TAB_HERO[tab] ?? FALLBACK;

interface DashboardSubtitleProps {
  /** Active tab id; drives the copy. */
  tab: string;
  superAdmin: boolean;
}

/** The subtitle under the Dashboard H1. The scope sentence is desktop-only, as PageHeader's is. */
const DashboardSubtitle: React.FC<DashboardSubtitleProps> = ({ tab, superAdmin }) => {
  const copy = heroCopyFor(tab);
  return (
    <>
      {typeof copy.title === 'function' ? copy.title(superAdmin) : copy.title}
      <span className="cc-page-header__subtitle-more"> {copy.text(superAdmin)}</span>
    </>
  );
};

export default DashboardSubtitle;
